
import React, { useState, useEffect } from 'react';
import { client } from '../api/client';
import { useNavigate, useLocation } from 'react-router-dom';
import { Sparkles, Mail, ArrowRight, Loader2, CheckCircle } from 'lucide-react';
import { motion } from 'framer-motion';

export const Login = () => {
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(false);
    const [error, setError] = useState('');
    const navigate = useNavigate();
    const location = useLocation();

    // Check if already logged in
    useEffect(() => {
        client.auth.getSession().then(({ data }) => {
            if (data?.user) {
                const from = (location.state as any)?.from?.pathname || '/';
                navigate(from, { replace: true });
            }
        });
    }, [navigate, location]);

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!email) return;

        setLoading(true);
        setError('');

        // Use configured site URL or fallback to current origin
        const redirectTo = import.meta.env.VITE_SITE_URL || window.location.origin;
        console.log('Redirecting auth to:', redirectTo);

        try {
            const { error } = await client.auth.signInWithOtp({
                email,
                options: {
                    emailRedirectTo: redirectTo,
                },
            });

            if (error) throw error;
            setSent(true);
        } catch (err: any) {
            console.error(err);
            setError(err.message || 'Failed to send login link');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-obsidian flex flex-col items-center justify-center p-6 relative overflow-hidden">
            {/* Background Effects */}
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
                <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-copper-500/10 rounded-full blur-[100px] translate-x-1/2 -translate-y-1/2" />
                <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-sage-500/10 rounded-full blur-[100px] -translate-x-1/2 translate-y-1/2" />
            </div>

            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="w-full max-w-md relative z-10"
            >
                <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-copper-500 to-copper-600 shadow-elevated mb-6">
                        <Sparkles className="w-8 h-8 text-cream-50" />
                    </div>
                    <h1 className="text-4xl font-display font-bold text-cream-50 mb-2">CardBoss</h1>
                    <p className="text-cream-200/60">The professional platform for card collectors</p>
                </div>

                <div className="card-premium p-8 backdrop-blur-xl">
                    {sent ? (
                        <div className="text-center py-4">
                            <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
                                <CheckCircle className="w-8 h-8 text-success" />
                            </div>
                            <h3 className="text-xl font-semibold text-cream-50 mb-2">Check your email</h3>
                            <p className="text-cream-200/60 mb-6">
                                We've sent a magic link to <span className="text-cream-100 font-medium">{email}</span>
                            </p>
                            <button
                                onClick={() => setSent(false)}
                                className="text-copper-400 hover:text-copper-300 text-sm font-medium transition-colors"
                            >
                                Use a different email
                            </button>
                        </div>
                    ) : (
                        <form onSubmit={handleLogin} className="space-y-6">
                            <div className="space-y-2">
                                <label htmlFor="email" className="text-sm font-medium text-cream-200/80 ml-1">
                                    Email Address
                                </label>
                                <div className="relative group">
                                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-cream-200/40 group-focus-within:text-copper-400 transition-colors" />
                                    <input
                                        id="email"
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="Enter email to sign in or register"
                                        className="w-full bg-black/40 border border-white/10 rounded-xl py-3 pl-12 pr-4 text-cream-50 placeholder:text-cream-200/20 focus:outline-none focus:border-copper-500/50 transition-all"
                                        required
                                    />
                                </div>
                            </div>

                            {error && (
                                <div className="p-3 rounded-lg bg-danger/10 border border-danger/20 text-danger text-sm">
                                    {error}
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full bg-copper-500 hover:bg-copper-400 disabled:opacity-50 disabled:cursor-not-allowed text-cream-50 font-semibold py-3.5 rounded-xl flex items-center justify-center gap-2 transition-all shadow-lg shadow-copper-500/20 hover:shadow-copper-500/30 hover:-translate-y-0.5"
                            >
                                {loading ? (
                                    <Loader2 className="w-5 h-5 animate-spin" />
                                ) : (
                                    <>
                                        Sign In / Register
                                        <ArrowRight className="w-5 h-5" />
                                    </>
                                )}
                            </button>

                            <p className="text-center text-xs text-cream-200/40">
                                By signing in, you agree to our Terms of Service and Privacy Policy.
                            </p>
                        </form>
                    )}
                </div>
            </motion.div>
        </div>
    );
};
